/**
 * RecruitHome - the Recruit area's study picker, /crm/recruit (docs/plans/crm-recruitment-list-plan.md
 * Part A section 3 step 1, decisions 12 and 13, B3, B4).
 *
 * One card per study that has a recruitment list (recruiting, or paused and shown greyed; closed and
 * archived studies are not served) and at least one open lead (decision 12; the api lists every such
 * study and leaves that choice here, so a "No open leads (N)" chip brings the rest back rather than
 * hiding them without a word), from GET /api/crm/recruit/studies: the study's name (the
 * recruiter-facing internal title, with the public title under it when they differ), its status,
 * its open leads, the caller's own open leads, "needs action now" where the server computed it (only
 * for the studies the caller is on the roster of; null elsewhere, and a null renders nothing, never
 * a zero), and the study's recruiters. The caller's studies come first, then the rest, each group by
 * name. Every card is a real link to that study's recruitment list (/crm/recruit/<slug>), so
 * right-click, middle-click and Ctrl-click open it in a new tab as everywhere in the CRM.
 *
 * Anyone in the CRM may open any study's list (decision 13): the roster only orders this page.
 * An api that does not serve the recruitment list yet (a 404 or 5xx: the outreach rule) gets a quiet
 * "not available yet" state instead of an error box.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardCheck, Loader2, Users } from 'lucide-react';
import PageHeader, { RefreshButton } from '../components/PageHeader';
import { FilterChip } from '../components/filters';
import { RowLink } from '../components/shared';
import { Alert } from '../components/ui';
import EmptyState from '../../ui/EmptyState';
import { studyStatusMeta, useIsCRMAdmin } from '../lib';
import { studyInternalLabel } from '../types';
import { isRecruitUnavailable, listRecruitStudies, recruitErrorMessage } from './api';
import type { RecruitRosterMember, RecruitStudyCard } from './types';

// ---------------------------------------------------------------------------------------------
// Which studies have a recruitment list, and where it lives
// ---------------------------------------------------------------------------------------------

/** The study statuses that have a recruitment list (decision 12; paused shows greyed). */
export const RECRUIT_LIST_STATUSES: readonly string[] = ['recruiting', 'paused'];

/** The recruitment list of the study with this slug. */
export function recruitSheetPath(slug: string): string {
  return `/crm/recruit/${encodeURIComponent(slug)}`;
}

/**
 * The study's recruitment list, or null when it has none: only a real CRM study (a crm.studies
 * row, so it has an id; a lead-only slug has none) that is recruiting or paused and not archived.
 * That is exactly the set GET /recruit/studies serves and the sheet resolves its slug through
 * (plan B13), so a link built here never lands on "this study has no recruitment list". Used by
 * the study page's header button and by the By study section headers.
 */
export function recruitListHref(study: {
  id?: string | null;
  slug?: string | null;
  status?: string | null;
  archived_at?: string | null;
}): string | null {
  if (!study.id || !study.slug) return null;
  if (!study.status || !RECRUIT_LIST_STATUSES.includes(study.status)) return null;
  if (study.archived_at) return null;
  return recruitSheetPath(study.slug);
}

// ---------------------------------------------------------------------------------------------
// Ordering and words
// ---------------------------------------------------------------------------------------------

const studyName = (s: Pick<RecruitStudyCard, 'title' | 'internal_title' | 'slug'>): string =>
  studyInternalLabel(s) || s.slug;

const byName = (a: RecruitStudyCard, b: RecruitStudyCard): number =>
  studyName(a).localeCompare(studyName(b), undefined, { sensitivity: 'base', numeric: true })
  || (a.slug < b.slug ? -1 : a.slug > b.slug ? 1 : 0);

/** The caller's studies (their roster) first, then the rest; each group by name. */
function splitByRoster(studies: readonly RecruitStudyCard[]): { yours: RecruitStudyCard[]; others: RecruitStudyCard[] } {
  const yours = studies.filter((s) => s.on_roster === true).sort(byName);
  const others = studies.filter((s) => s.on_roster !== true).sort(byName);
  return { yours, others };
}

const countOf = (n: unknown): number => (typeof n === 'number' && Number.isFinite(n) ? n : 0);

/** "Dana Reyes, Sam Lee, Priya Shah +2": up to three names, then how many more. */
function rosterWords(roster: readonly RecruitRosterMember[] | null | undefined): { line: string; all: string } {
  const members = Array.isArray(roster) ? roster : [];
  if (members.length === 0) return { line: 'No recruiters on the roster yet', all: '' };
  const names = members.map((m) => (m?.full_name || '').trim()).filter(Boolean);
  if (names.length === 0) {
    const n = members.length;
    return { line: `${n.toLocaleString()} recruiter${n === 1 ? '' : 's'}`, all: '' };
  }
  const shown = names.slice(0, 3);
  const more = members.length - shown.length;
  return {
    line: more > 0 ? `${shown.join(', ')} +${more.toLocaleString()}` : shown.join(', '),
    all: names.join(', '),
  };
}

// ---------------------------------------------------------------------------------------------
// One study card
// ---------------------------------------------------------------------------------------------

function Stat({ label, value, title, strong = false }: { label: string; value: number; title: string; strong?: boolean }) {
  return (
    <div className="flex min-w-0 flex-col-reverse" title={title}>
      <dt className="truncate text-xs text-gray-600 dark:text-gray-400">{label}</dt>
      <dd
        className={`text-lg font-semibold leading-6 tabular-nums ${
          strong ? 'text-primary-700 dark:text-primary-400' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value.toLocaleString()}
      </dd>
    </div>
  );
}

function StudyCard({ study }: { study: RecruitStudyCard }) {
  const name = studyName(study);
  const publicTitle = study.internal_title && study.title && study.internal_title !== study.title ? study.title : null;
  const meta = studyStatusMeta(study.status);
  const paused = study.status === 'paused';
  const needs = typeof study.needs_action === 'number' && Number.isFinite(study.needs_action) ? study.needs_action : null;
  const roster = rosterWords(study.roster);
  return (
    <li>
      <RowLink
        to={recruitSheetPath(study.slug)}
        title={`Open the ${name} recruitment list`}
        className={`flex h-full min-h-[44px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4 transition-all duration-150 hover:border-gray-300 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:hover:border-gray-600 ${
          paused ? 'opacity-60 hover:opacity-100' : ''
        }`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <h3 className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{name}</h3>
            {publicTitle && (
              <p className="truncate text-xs text-gray-600 dark:text-gray-400" title="Public title">{publicTitle}</p>
            )}
          </div>
          <span className={`inline-flex flex-shrink-0 items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${meta.badge}`}>
            {meta.label}
          </span>
        </div>
        <dl className="grid grid-cols-3 gap-2">
          <Stat label="Open leads" value={countOf(study.open)} title="Leads still open on this study (New through Screened)" />
          <Stat label="Yours" value={countOf(study.mine)} title="Open leads you own" />
          {needs !== null && (
            <Stat
              label="Needs action now"
              value={needs}
              strong={needs > 0}
              title="Open leads with something due now: the rows the recruitment list puts first"
            />
          )}
        </dl>
        <p className="mt-auto flex min-w-0 items-center gap-1.5 text-xs text-gray-600 dark:text-gray-400" title={roster.all || undefined}>
          <Users className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
          <span className="min-w-0 truncate">{roster.line}</span>
        </p>
      </RowLink>
    </li>
  );
}

const GRID = 'grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4';
const GROUP_LABEL = 'mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500';

// ---------------------------------------------------------------------------------------------
// The page
// ---------------------------------------------------------------------------------------------

export default function RecruitHome() {
  const isAdmin = useIsCRMAdmin();
  const [studies, setStudies] = useState<RecruitStudyCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [unavailable, setUnavailable] = useState(false);
  // Studies with no open lead are left off (decision 12) until this is on.
  const [showEmpty, setShowEmpty] = useState(false);
  // A newer load supersedes an older one still in flight (Refresh pressed twice).
  const genRef = useRef(0);

  const load = useCallback(async () => {
    const gen = ++genRef.current;
    setLoading(true);
    try {
      const rows = await listRecruitStudies();
      if (gen !== genRef.current) return;
      setStudies(rows.filter((s) => s && typeof s.id === 'string' && typeof s.slug === 'string' && s.slug));
      setError(null);
      setUnavailable(false);
      setLoaded(true);
    } catch (e) {
      if (gen !== genRef.current) return;
      if (isRecruitUnavailable(e)) setUnavailable(true);
      else setError(recruitErrorMessage(e, "Couldn't load the studies. Please try again."));
    } finally {
      if (gen === genRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Nothing that lands after the page closes may write into it.
    return () => { genRef.current += 1; };
  }, [load]);

  const withOpen = useMemo(() => studies.filter((s) => countOf(s.open) > 0), [studies]);
  const hiddenCount = studies.length - withOpen.length;
  const shown = showEmpty ? studies : withOpen;
  const { yours, others } = useMemo(() => splitByRoster(shown), [shown]);

  if (unavailable) {
    return (
      <div className="space-y-4">
        <PageHeader icon={ClipboardCheck} title="Recruit" />
        <EmptyState
          variant="card"
          icon={ClipboardCheck}
          title="The recruitment list isn't available yet"
          hint="This part of the CRM is still being switched on, or couldn't be reached. Check back soon."
          action={
            <button
              type="button"
              onClick={() => { setUnavailable(false); load(); }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              Try again
            </button>
          }
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PageHeader
        icon={ClipboardCheck}
        title="Recruit"
        count={loaded ? shown.length : null}
        subtitle="Pick a study to work its recruitment list, the leads that need action first. Your studies come first."
        actions={
          <>
            {hiddenCount > 0 && (
              <FilterChip
                active={showEmpty}
                onClick={() => setShowEmpty((v) => !v)}
                title={showEmpty ? 'Hide the studies with no open leads' : 'Also show the studies with no open leads'}
              >
                No open leads ({hiddenCount.toLocaleString()})
              </FilterChip>
            )}
            <RefreshButton onClick={() => { load(); }} loading={loading} label="Refresh studies" />
          </>
        }
      />

      {error && (
        <Alert
          tone="error"
          icon={false}
          actions={<button type="button" onClick={() => { load(); }} className="font-medium underline hover:no-underline">Retry</button>}
        >
          {error}
        </Alert>
      )}

      {loading && !loaded ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600 dark:text-primary-400" aria-label="Loading studies" />
        </div>
      ) : loaded && studies.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ClipboardCheck}
          title="No studies are recruiting"
          hint="A study shows up here with its recruitment list once it is set to Recruiting (or Paused) on its study page under Settings."
          action={
            isAdmin ? (
              <Link
                to="/crm/settings/studies"
                className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Go to Studies
              </Link>
            ) : undefined
          }
        />
      ) : loaded && shown.length === 0 ? (
        <EmptyState
          variant="card"
          icon={ClipboardCheck}
          title="No open leads on any recruiting study"
          hint="No recruiting or paused study has an open lead right now. New leads show up here as they come in."
          action={
            <button
              type="button"
              onClick={() => setShowEmpty(true)}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
            >
              Show the studies anyway
            </button>
          }
        />
      ) : (
        <div className="space-y-5">
          {yours.length > 0 && (
            <section aria-labelledby="recruit-your-studies">
              <h2 id="recruit-your-studies" className={GROUP_LABEL}>Your studies</h2>
              <ul className={GRID}>
                {yours.map((s) => <StudyCard key={s.id} study={s} />)}
              </ul>
            </section>
          )}
          {others.length > 0 && (
            <section aria-labelledby={yours.length > 0 ? 'recruit-other-studies' : undefined} aria-label={yours.length > 0 ? undefined : 'Studies'}>
              {yours.length > 0 && <h2 id="recruit-other-studies" className={GROUP_LABEL}>Other studies</h2>}
              <ul className={GRID}>
                {others.map((s) => <StudyCard key={s.id} study={s} />)}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
